import React, { useRef, useState } from 'react';
import { FONT, colors, radii } from '../../theme';
import { S } from './dashboardStyles';
import { Download, Upload, AlertTriangle, Check } from 'lucide-react';
import { messageStore } from '../../services/messageStore.mjs';
import { collectExport, makeArchive, restoreArchive } from '../../services/historyTransfer.mjs';
import { exportMaster, adoptMaster } from '../../services/masterIdentity.mjs';
import { secureSyncStorage } from '../../services/secureStore.mjs';
import { fetchFileBytes } from '../../services/fileFetch';
import { saveBytes } from '../../services/fileSave.mjs';
import { DEFAULT_ITERATIONS } from '../../services/historyArchive.mjs';

/**
 * Export and import the conversation history.
 *
 * Not a convenience. With the ratchet, message keys are destroyed after use and
 * a new phone starts with an empty chat — so this file is the only way a
 * conversation survives changing devices. The module has existed since ADR-016
 * stage 6; until this screen there was no way for a person to reach it, which
 * made the export exactly as useful as not having one.
 *
 * Everything here is deliberately blunt: the passphrase cannot be recovered,
 * the file is the only copy, and a picture the server has already dropped is
 * named rather than quietly left out.
 */
export default function HistoryArchiveCard({ userId }) {
  const [pass, setPass] = useState('');
  const [pass2, setPass2] = useState('');
  const [busy, setBusy] = useState('');            // '' | 'collect' | 'pack' | 'unpack'
  const [progress, setProgress] = useState(0);
  const [error, setError] = useState('');
  const [done, setDone] = useState(null);
  const [importPass, setImportPass] = useState('');
  const [importFile, setImportFile] = useState(null);
  const [imported, setImported] = useState(null);
  const linkRef = useRef(null);


  // A sealed attachment (SEC-04) is decrypted here, before packing: the
  // export's protection is the passphrase, and packing ciphertext whose key
  // lives in a message envelope would tie the archive's readability to the
  // messages surviving too.
  const fetchAttachment = (fileId, fileKey) => fetchFileBytes(fileId, fileKey);

  const runExport = async () => {
    setError(''); setDone(null);
    if (pass.length < 8) { setError('Парольная фраза должна быть не короче 8 символов'); return; }
    if (pass !== pass2) { setError('Фразы не совпадают'); return; }
    try {
      setBusy('collect'); setProgress(0);
      const collected = await collectExport({
        myUserId: userId, archive: messageStore, fetchAttachment,
        onProgress: (d, total) => setProgress(total ? Math.round((d / total) * 100) : 100),
      });
      setBusy('pack'); setProgress(0);
      const bytes = await makeArchive({
        passphrase: pass, myUserId: userId, collected,
        // The account key that signs the device list. Keeping it on one device
        // is what stops a hostile server adding a device; the same fact would
        // make a lost phone a permanent inability to add another, so it goes
        // where the history already goes. It also means this file is now the
        // account, not only the conversations.
        masterSecret: exportMaster(secureSyncStorage, userId),
        onProgress: (d, total) => setProgress(total ? Math.round((d / total) * 100) : 100),
      });

      const stamp = new Date().toISOString().slice(0, 10);
      // Where it goes is the shell's business — and on a phone it is a system
      // sheet, not a download. This used to be a blob click that WebViews
      // ignore, which is why no phone ever produced an archive (fileSave.mjs).
      const via = saveBytes({ bytes, filename: `voxiox-history-${stamp}.vox`, anchor: linkRef.current });

      // Remembered so the root screen can stop nagging — and start again when
      // the archive is old enough to be worth remaking. Written only after the
      // file has actually left: a remembered date for a file nobody has is
      // worse than the reminder it silences.
      try { localStorage.setItem('voxiox_archive_last_at', new Date().toISOString()); } catch (e) {}
      setDone({ ...collected, bytes: bytes.length, via });
      setPass(''); setPass2('');
    } catch (e) {
      setError(e?.message || 'Не удалось собрать архив');
    } finally {
      setBusy(''); setProgress(0);
    }
  };

  const runImport = async () => {
    setError(''); setImported(null);
    if (!importFile) { setError('Выберите файл архива'); return; }
    if (!importPass) { setError('Введите парольную фразу'); return; }
    try {
      setBusy('unpack'); setProgress(0);
      const bytes = new Uint8Array(await importFile.arrayBuffer());
      const result = await restoreArchive({
        passphrase: importPass, bytes, archive: messageStore,
        // Refuses to overwrite a master this device already holds: two masters
        // for one account is the state where half the peers pin one and half
        // the other, and no screen anywhere would explain it.
        adoptMaster: async (secret, forUserId) => adoptMaster(secureSyncStorage, forUserId, secret),
        onProgress: (d, total) => setProgress(total ? Math.round((d / total) * 100) : 100),
      });
      setImported(result);
      setImportPass(''); setImportFile(null);
    } catch (e) {
      // The most likely cause by far, and the least helpful message on its own.
      setError(/decrypt|open|MAC|расшифров/i.test(e?.message || '')
        ? 'Не открылось: не та парольная фраза или файл повреждён'
        : (e?.message || 'Не удалось прочитать архив'));
    } finally {
      setBusy(''); setProgress(0);
    }
  };

  const label = busy === 'collect' ? 'Собираю переписку и вложения…'
    : busy === 'pack' ? 'Шифрую архив…'
    : busy === 'unpack' ? 'Открываю архив…' : '';

  return (
    <div style={{ ...S.settingsCard, padding: 16, display: 'flex', flexDirection: 'column', gap: 14 }}>
      <div>
        <div style={{ fontFamily: FONT, fontSize: 15, fontWeight: 600, color: colors.gray900 }}>
          Архив переписки
        </div>
        <div style={{ fontFamily: FONT, fontSize: 13, color: colors.gray400, marginTop: 4, lineHeight: 1.45 }}>
          Шифрование устроено так, что ключ каждого сообщения уничтожается сразу после
          прочтения — поэтому новый телефон начинает с пустого чата. Этот файл —
          единственный способ перенести переписку. Сделайте его <b>до</b> смены телефона.
        </div>
      </div>

      {/* ── Экспорт ── */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        <input
          type="password" value={pass} onChange={(e) => setPass(e.target.value)}
          placeholder="Парольная фраза (от 8 символов)" style={S.input} autoComplete="new-password"
        />
        <input
          type="password" value={pass2} onChange={(e) => setPass2(e.target.value)}
          placeholder="Ещё раз" style={S.input} autoComplete="new-password"
        />
        <div style={{
          display: 'flex', gap: 8, alignItems: 'flex-start',
          background: 'rgba(220,120,40,0.12)', borderRadius: radii.sm, padding: '10px 12px',
        }}>
          <AlertTriangle size={16} color={colors.gray900} style={{ flexShrink: 0, marginTop: 1 }} />
          <div style={{ fontFamily: FONT, fontSize: 12.5, color: colors.gray900, lineHeight: 1.4 }}>
            Фразу восстановить нельзя — ни нами, ни кем-либо ещё. Забыли её — архив
            превращается в набор случайных байт.
          </div>
        </div>
        <button onClick={runExport} disabled={!!busy}
          style={{ ...S.primaryBtn, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, opacity: busy ? 0.6 : 1 }}>
          <Download size={16} /> Сохранить архив
        </button>
      </div>

      {/* ── Импорт ── */}
      <div style={{ borderTop: `1px solid ${colors.gray200}`, paddingTop: 14, display: 'flex', flexDirection: 'column', gap: 8 }}>
        <div style={{ fontFamily: FONT, fontSize: 13.5, fontWeight: 600, color: colors.gray900 }}>
          Восстановить из архива
        </div>
        <input type="file" accept=".vox,application/octet-stream"
          onChange={(e) => setImportFile(e.target.files?.[0] || null)}
          style={{ fontFamily: FONT, fontSize: 13, color: colors.gray900 }} />
        <input type="password" value={importPass} onChange={(e) => setImportPass(e.target.value)}
          placeholder="Парольная фраза архива" style={S.input} autoComplete="off" />
        <button onClick={runImport} disabled={!!busy}
          style={{
            background: colors.gray50, border: `1px solid ${colors.gray200}`, color: colors.gray900,
            padding: '12px 20px', fontSize: 15, fontWeight: 500, cursor: 'pointer',
            borderRadius: radii.sm, fontFamily: FONT,
            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, opacity: busy ? 0.6 : 1,
          }}>
          <Upload size={16} /> Открыть архив
        </button>
        <div style={{ fontFamily: FONT, fontSize: 12, color: colors.gray400, lineHeight: 1.4 }}>
          Переписка добавляется к тому, что уже есть на этом устройстве — ничего не стирается.
        </div>
      </div>

      {/* ── Состояние ── */}
      {busy && (
        <div style={{ fontFamily: FONT, fontSize: 13, color: colors.gray900 }}>
          {label} {progress > 0 && `${progress}%`}
          <div style={{ height: 4, background: colors.gray200, borderRadius: 2, marginTop: 6, overflow: 'hidden' }}>
            <div style={{ height: '100%', width: `${progress}%`, background: colors.gray900, transition: 'width .2s' }} />
          </div>
          {busy === 'pack' && (
            <div style={{ fontSize: 12, color: colors.gray400, marginTop: 6 }}>
              Ключ выводится {DEFAULT_ITERATIONS.toLocaleString('ru-RU')} итерациями — на старом
              телефоне это может занять секунды. Так и задумано: столько же придётся потратить
              тому, кто будет подбирать фразу.
            </div>
          )}
        </div>
      )}

      {error && (
        <div style={{ fontFamily: FONT, fontSize: 13, color: colors.red }}>{error}</div>
      )}

      {done && (
        <div style={{ fontFamily: FONT, fontSize: 13, color: colors.gray900, lineHeight: 1.5 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontWeight: 600 }}>
            <Check size={16} /> {done.via === 'native' ? 'Архив готов' : 'Архив сохранён'}
          </div>
          <div style={{ color: colors.gray400 }}>
            {done.conversationCount} диалог(ов), {done.messageCount} сообщений,
            {' '}{done.attachments.length} вложений, {Math.round(done.bytes / 1024)} КБ
            {done.via === 'native' && ' — выберите, куда его положить'}
          </div>
          {done.missing.length > 0 && (
            <div style={{ color: colors.red, marginTop: 4 }}>
              {done.missing.length} вложени(й) сервер уже не отдаёт — в архив они не попали.
              Эти картинки не восстановятся ни на каком устройстве.
            </div>
          )}
        </div>
      )}

      {imported && (
        <div style={{ fontFamily: FONT, fontSize: 13, color: colors.gray900, lineHeight: 1.5 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontWeight: 600 }}>
            <Check size={16} /> Архив открыт
          </div>
          <div style={{ color: colors.gray400 }}>
            {imported.conversations} диалог(ов), {imported.restoredMessages} сообщений,
            {' '}{imported.restoredAttachments} вложений. Создан {new Date(imported.exportedAt).toLocaleString('ru-RU')}.
          </div>
        </div>
      )}

      {/* The click target for the download. A blob URL is set on it just
          before the click, because that is the only way a browser hands a
          file to a person without a server involved. */}
      <a ref={linkRef} style={{ display: 'none' }} href="/" aria-hidden="true">архив</a>
    </div>
  );
}
